// Web Push: registra el service worker y suscribe el navegador para
// recibir notificaciones de recordatorios aunque la app este cerrada.
import { api } from "./api";

/** Convierte la llave VAPID (base64url) a Uint8Array para applicationServerKey. */
function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

const DEVICE_ID_KEY = "ark_push_device_id";

/**
 * ID estable de ESTE navegador (localStorage). Identifica el equipo aunque
 * el endpoint de la suscripción rote, para que el backend reemplace la fila
 * vieja en vez de acumular zombies. Se crea la primera vez que se llama.
 */
export function getDeviceId(): string {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `dev-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return "dev-sin-storage";
  }
}

export type EstadoPush = {
  soportado: boolean;
  permiso: NotificationPermission | null;
  suscrito: boolean;
};

/** Estado de Web Push en ESTE navegador: soporte, permiso y suscripción. */
export async function estadoPush(): Promise<EstadoPush> {
  if (
    typeof navigator === "undefined" ||
    !("serviceWorker" in navigator) ||
    typeof window === "undefined" ||
    !("PushManager" in window) ||
    typeof Notification === "undefined"
  ) {
    return { soportado: false, permiso: null, suscrito: false };
  }
  let suscrito = false;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    suscrito = sub !== null;
  } catch {
    suscrito = false;
  }
  return { soportado: true, permiso: Notification.permission, suscrito };
}

/**
 * Asegura que este navegador este suscrito a Web Push y registrado en el
 * backend. Idempotente: se puede llamar varias veces. No hace nada si el
 * permiso de notificaciones no esta concedido o el navegador no soporta
 * push. Nunca lanza — los errores solo se loguean.
 */
export async function activarWebPush(): Promise<void> {
  try {
    if (
      typeof navigator === "undefined" ||
      !("serviceWorker" in navigator) ||
      typeof window === "undefined" ||
      !("PushManager" in window) ||
      typeof Notification === "undefined" ||
      Notification.permission !== "granted"
    ) {
      return;
    }
    const reg = await navigator.serviceWorker.register("/sw.js");
    await navigator.serviceWorker.ready;

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      const { public_key } = await api.get<{ public_key: string }>(
        "/api/push/vapid-public-key",
      );
      if (!public_key) return; // VAPID no configurado en el server
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(public_key) as BufferSource,
      });
    }
    const json = sub.toJSON();
    if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return;
    await api.post("/api/push/subscribe", {
      endpoint: json.endpoint,
      p256dh: json.keys.p256dh,
      auth: json.keys.auth,
      device_id: getDeviceId(),
    });
  } catch (e) {
    console.warn("activarWebPush fallo:", e);
  }
}
